import { useEffect, useRef } from 'react'

const isOrContainsNode = (
  ctx: HTMLElement | null,
  target: EventTarget | null
) => {
  return ctx
    ? ctx === target || (target instanceof Node && ctx.contains(target))
    : false
}

/**
 * This hook is a workaround for a bug in Radix DropdownMenu that
 * prevents 'mouseup' event handlers from firing when DropdownMenu.Trigger
 * is clicked.
 *
 * For components with dropdowns or popovers, they must listen to
 * 'pointerdown'/'pointerup' events in order to properly respond to
 * an outside click on a Radix DropdownMenu.
 *
 * Ideally this would be fixed in Radix's event handlers, and this hook
 * should be made obselete once the linked bug is fixed.
 *
 * @see https://github.com/radix-ui/primitives/issues/1903
 */
export const useRadixDropdownBlurHelper = (
  /* True when the component's popover is open. */
  isOpen: boolean,
  /* Refs for elements that will be treated as "inside clicks". */
  refs: React.RefObject<HTMLElement>[],
  /* Callback to invoke when an outside click occurs. */
  handleBlur: () => void
) => {
  const pointerRef = useRef({ isPointerDown: false })

  useEffect(() => {
    const onPointerDown = () => {
      pointerRef.current.isPointerDown = true
    }
    const onPointerUp = (e: PointerEvent) => {
      pointerRef.current.isPointerDown = false
      const elements = refs.map((ref) => ref.current)
      const target = e.target
      if (
        isOpen &&
        !elements.some(
          (ctx) =>
            isOrContainsNode(ctx, target) ||
            isOrContainsNode(ctx, document.activeElement)
        )
      ) {
        handleBlur()
      }
    }

    window.addEventListener('pointerdown', onPointerDown)
    window.addEventListener('pointerup', onPointerUp)

    return () => {
      window.removeEventListener('pointerdown', onPointerDown)
      window.removeEventListener('pointerup', onPointerUp)
    }
  }, [isOpen, refs, handleBlur])

  return pointerRef
}
